import { CivilLawSubjectType, RoleInCompanyType } from 'api/types/entity';
import { GridFields } from 'components';
import { isFinanceProject } from 'config';
import { useTranslation } from 'libs/i18n';
import { Stack, Typography } from 'libs/ui';
import {
  getToday,
  getTodayWithResetHours,
} from 'modules/client/common/helpers';

import {
  dateOfBirthMaxDate,
  dateOfBirthMinDate,
  Field,
  useFormContext,
} from '../../../forms';

export const Representative = () => {
  const { t } = useTranslation();
  const { values } = useFormContext();
  const isFinance = isFinanceProject();

  const showCivilLawSubject =
    isFinance &&
    values?.representative?.role?.value ===
      RoleInCompanyType.AuthorizedRepresentative;

  const showCompanyFullName =
    showCivilLawSubject &&
    values?.representative?.civilLawSubject?.value ===
      CivilLawSubjectType.JuridicalPerson;

  return (
    <Stack spacing={12}>
      <Typography variant="subtitle">
        {t('client.createLegalClient.representative')}
      </Typography>

      <GridFields>
        <Field.Autocomplete
          showRequiredMark
          label={t('client.createLegalClient.fields.role.label')}
          name="representative.role"
          optionLabelKey="label"
          options={Object.values(RoleInCompanyType).map((v) => ({
            label: t(`client.legalRepresentative.roleInCompany.roles.${v}`),
            value: v,
          }))}
          optionValueKey="value"
          placeholder={t('client.createLegalClient.fields.role.placeholder')}
          variant="select"
        />

        {showCivilLawSubject && (
          <Field.Autocomplete
            fullWidth
            showRequiredMark
            label={t(
              'client.legalRepresentative.roleInCompany.civilLawSubject.label'
            )}
            name="representative.civilLawSubject"
            optionLabelKey="label"
            options={Object.values(CivilLawSubjectType).map((v) => ({
              label: t(
                `client.legalRepresentative.roleInCompany.civilLawSubjects.${v}`
              ),
              value: v,
            }))}
            optionValueKey="value"
            placeholder={t(
              'client.legalRepresentative.roleInCompany.civilLawSubject.placeholder'
            )}
            variant="select"
          />
        )}

        <Field.DatePicker
          fullWidth
          showRequiredMark
          label={t('client.createLegalClient.fields.dateOfIssue.label')}
          maxDate={getToday()}
          name="representative.dateOfIssue"
          placeholder={t(
            'client.createLegalClient.fields.dateOfIssue.placeholder'
          )}
        />
        <Field.DatePicker
          fullWidth
          disableFuture={false}
          label={t('client.createLegalClient.fields.dateOfExpiration.label')}
          minDate={getTodayWithResetHours()}
          name="representative.dateOfExpiration"
          placeholder={t(
            'client.createLegalClient.fields.dateOfExpiration.placeholder'
          )}
        />

        {showCompanyFullName && (
          <Field.Text
            fullWidth
            showRequiredMark
            label={t('client.createLegalClient.fields.companyFullName.label')}
            name="representative.companyFullName"
            placeholder={t(
              'client.createLegalClient.fields.companyFullName.placeholder'
            )}
          />
        )}

        <Field.Text
          fullWidth
          showRequiredMark
          autoComplete="off"
          label={t('client.createLegalClient.fields.email.label')}
          name="representative.email"
          placeholder={t('client.createLegalClient.fields.email.placeholder')}
        />

        <Field.Text
          fullWidth
          showRequiredMark
          label={t('client.createLegalClient.fields.firstName.label')}
          name="representative.firstName"
          placeholder={t(
            'client.createLegalClient.fields.firstName.placeholder'
          )}
        />
        <Field.Text
          fullWidth
          showRequiredMark
          label={t('client.createLegalClient.fields.lastName.label')}
          name="representative.lastName"
          placeholder={t(
            'client.createLegalClient.fields.lastName.placeholder'
          )}
        />
        <Field.Text
          fullWidth
          label={t('client.createLegalClient.fields.middleName.label')}
          name="representative.middleName"
          placeholder={t(
            'client.createLegalClient.fields.middleName.placeholder'
          )}
        />
        <Field.DatePicker
          fullWidth
          showRequiredMark
          label={t('client.createLegalClient.fields.dateOfBirth.label')}
          maxDate={dateOfBirthMaxDate}
          minDate={dateOfBirthMinDate}
          name="representative.dateOfBirth"
          placeholder={t(
            'client.createLegalClient.fields.dateOfBirth.placeholder'
          )}
        />

        <Field.Phone
          fullWidth
          showRequiredMark
          label={t('client.createLegalClient.fields.phoneNumber.label')}
          maxLength={22}
          name="representative.phoneNumber"
          placeholder={t(
            'client.createLegalClient.fields.phoneNumber.placeholder'
          )}
          type="phone"
        />
      </GridFields>
    </Stack>
  );
};
